import { useState, useRef, useEffect, useCallback } from 'react';

interface WhiteboardProps {
  isOpen: boolean;
  onClose: () => void;
}

const Whiteboard = ({ isOpen, onClose }: WhiteboardProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });

  // Initialize canvas with proper scaling
  const initializeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get the device pixel ratio for crisp lines
    const ratio = Math.ceil(window.devicePixelRatio);
    
    // Set actual canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    
    // Scale the context to ensure correct drawing operations
    ctx.scale(ratio, ratio);
    
    // Set canvas style size to maintain layout
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';

    // Configure drawing context for smooth lines
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.imageSmoothingEnabled = true;
  }, []);

  // Initialize canvas when component mounts or becomes visible
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready
      setTimeout(initializeCanvas, 10);
    }
  }, [isOpen, initializeCanvas]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const whiteboardPanel = (event.target as Element)?.closest('.whiteboard-panel');
      
      if (isOpen && !whiteboardPanel) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Get accurate coordinates
  const getCoordinates = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  // Drawing functions
  const startDrawing = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const coords = getCoordinates(event);
    setIsDrawing(true);
    setLastPosition(coords);

    // Draw a small dot at the start point for single clicks
    ctx.beginPath();
    ctx.arc(coords.x, coords.y, 1.5, 0, Math.PI * 2);
    ctx.fill();
  };

  const draw = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const coords = getCoordinates(event);

    ctx.beginPath();
    ctx.moveTo(lastPosition.x, lastPosition.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();

    setLastPosition(coords);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Clear whiteboard
  const clearWhiteboard = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Clear the entire canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Reinitialize the drawing settings after clearing
    ctx.strokeStyle = '#2563eb';
    ctx.fillStyle = '#2563eb';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.imageSmoothingEnabled = true;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 right-0 h-full w-1/3 bg-white shadow-2xl border-l-2 border-gray-200 z-50 whiteboard-panel">
      {/* Header */}
      <div className="bg-blue-50 border-b border-gray-200 p-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">📝</span>
          <h3 className="text-lg font-semibold text-gray-800">Scratchpad</h3>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={clearWhiteboard}
            className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
          >
            🗑️ Clear
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            ✕ Close
          </button>
        </div>
      </div>

      {/* Canvas Area */}
      <div className="p-4 h-full">
        <canvas
          ref={canvasRef}
          className="border-2 border-gray-300 rounded-lg bg-white w-full shadow-inner"
          style={{ 
            height: 'calc(100vh - 140px)',
            cursor: 'crosshair',
            touchAction: 'none' // Prevent scrolling on touch devices
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
        
        {/* Instructions */}
        <div className="mt-3 text-xs text-gray-500 text-center">
          <p>🖊️ Click and drag to draw • 🗑️ Clear to erase • 👆 Click outside to close</p>
        </div>
      </div>
    </div>
  );
};

export default Whiteboard;